import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Table, Button, DatePicker, Segmented, Space,
  Typography, Spin, message, Tag,
} from 'antd';
import {
  DollarOutlined, ApiOutlined, TeamOutlined, DownloadOutlined,
  ReloadOutlined, CalendarOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { agentAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COLORS = ['#5470c6','#91cc75','#fac858','#ee6666','#73c0de','#3ba272','#fc8452','#9a60b4','#ea7ccc','#48c9b0'];

export default function ConsumptionStats() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [trendMode, setTrendMode] = useState('stack'); // stack=堆叠面积, line=折线对比

  const loadData = useCallback(async () => {
    if (!dateRange[0] || !dateRange[1]) return;
    setLoading(true);
    try {
      const res = await agentAPI.getConsumptionStats({
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
      });
      setStats(res.data);
    } catch (err) {
      message.error('加载数据失败，请重试');
    } finally { setLoading(false); }
  }, [dateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePreset = (key) => {
    const now = dayjs();
    const presets = {
      today: [now.startOf('day'), now],
      week: [now.startOf('week'), now],
      month: [now.startOf('month'), now],
      last30: [now.subtract(30, 'day'), now],
    };
    if (presets[key]) setDateRange(presets[key]);
  };

  const handleExport = () => {
    const rows = [['模型标识', '显示名称', '输入Token', '输出Token', '消费金额(元)', '请求数', '占比(%)']];
    (stats?.by_model || []).forEach(m => {
      rows.push([m.model, m.display_name, m.input_tokens, m.output_tokens,
        m.total_cost, m.request_count, m.percentage.toFixed(1)]);
    });
    const csv = '﻿' + rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `模型消费统计_${dateRange[0].format('YYYYMMDD')}-${dateRange[1].format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('CSV导出成功');
  };

  if (loading && !stats) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
      <Spin size="large" tip="加载统计数据中..." />
    </div>
  );

  const summary = stats?.summary || {};
  const byModel = stats?.by_model || [];
  const daily = stats?.daily || [];

  const pieOption = {
    tooltip: { trigger: 'item', formatter: (p) => `${p.name}<br/>¥${p.value.toFixed(2)} (${p.percent}%)` },
    legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['50%', '75%'], center: ['50%', '43%'],
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 3 },
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 16, fontWeight: 'bold' }, scaleSize: 10 },
      data: byModel.map((m, i) => ({
        name: m.display_name || m.model,
        value: parseFloat((m.total_cost || 0).toFixed(2)),
        itemStyle: { color: COLORS[i % COLORS.length] },
      })),
    }],
  };

  const modelNames = [...new Set(daily.flatMap(d => Object.keys(d.models || {})))];
  const isStack = trendMode === 'stack';
  const lineOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let html = `<b>${params[0].axisValue}</b><br/>`;
        let total = 0;
        params.forEach(p => { html += `${p.marker} ${p.seriesName}: ¥${p.value.toFixed(2)}<br/>`; total += p.value; });
        html += `<b style="color:#f5576c">合计: ¥${total.toFixed(2)}</b>`;
        return html;
      },
    },
    legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 70, right: 30, top: 40, bottom: 50, containLabel: true },
    xAxis: { type: 'category', boundaryGap: isStack, data: daily.map(d => dayjs(d.date).format('MM-DD')), axisLabel: { fontSize: 11 } },
    yAxis: {
      type: 'value',
      name: '消费金额（元）',
      nameGap: 16,
      nameTextStyle: { fontSize: 11, color: '#888', align: 'left' },
      axisLabel: { fontSize: 11, formatter: (v) => `¥${v}` },
    },
    series: modelNames.map((name, i) => ({
      name,
      type: 'line',
      smooth: true,
      symbol: isStack ? 'none' : 'circle',
      symbolSize: 5,
      stack: isStack ? 'Total' : undefined,
      areaStyle: isStack ? { opacity: 0.55 } : undefined,
      lineStyle: { width: isStack ? 1 : 2 },
      emphasis: { focus: 'series' },
      data: daily.map(d => parseFloat(((d.models || {})[name] || 0).toFixed(2))),
      color: COLORS[i % COLORS.length],
    })),
  };

  const barOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 105, right: 50, top: 10, bottom: 20 },
    xAxis: { type: 'value', name: '元', axisLabel: { formatter: '¥{value}' } },
    yAxis: {
      type: 'category',
      data: byModel.map(m => m.display_name || m.model).reverse(),
      axisLabel: { fontSize: 11, width: 95, overflow: 'truncate' },
    },
    series: [{
      type: 'bar',
      data: byModel.map((m, i) => ({
        value: parseFloat((m.total_cost || 0).toFixed(2)),
        itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [0, 4, 4, 0] },
      })).reverse(),
      label: { show: true, position: 'right', fontSize: 11, formatter: '¥{c}' },
    }],
  };

  const tableColumns = [
    { title: '显示名称', dataIndex: 'display_name', key: 'display_name', width: 150, fixed: 'left' },
    { title: '模型标识', dataIndex: 'model', key: 'model', width: 170, render: (t) => <Tag color="geekblue">{t}</Tag> },
    { title: '输入Token', dataIndex: 'input_tokens', key: 'input_tokens', align: 'right',
      render: (v) => (v || 0).toLocaleString(), sorter: (a, b) => (a.input_tokens||0) - (b.input_tokens||0) },
    { title: '输出Token', dataIndex: 'output_tokens', key: 'output_tokens', align: 'right',
      render: (v) => (v || 0).toLocaleString(), sorter: (a, b) => (a.output_tokens||0) - (b.output_tokens||0) },
    { title: '消费金额', dataIndex: 'total_cost', key: 'total_cost', align: 'right',
      render: (v) => <Text strong style={{ color: '#f5576c' }}>¥{parseFloat(v||0).toFixed(4)}</Text>,
      sorter: (a, b) => (a.total_cost||0) - (b.total_cost||0) },
    { title: '请求数', dataIndex: 'request_count', key: 'request_count', align: 'right', render: (v) => (v||0).toLocaleString() },
    {
      title: '占比', dataIndex: 'percentage', key: 'percentage', width: 130,
      render: (v) => {
        const pct = parseFloat(v || 0).toFixed(1);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #667eea, #764ba2)', borderRadius: 3, transition: 'width 0.5s ease' }} />
            </div>
            <Text style={{ fontSize: 12, minWidth: 38 }}>{pct}%</Text>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* 筛选器 */}
      <Card bordered={false} style={{ borderRadius: 14, marginBottom: 20 }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col>
            <Space size="middle" wrap>
              <CalendarOutlined style={{ fontSize: 18, color: '#667eea' }} />
              <Segmented
                options={[
                  { label: '今日', value: 'today' },
                  { label: '本周', value: 'week' },
                  { label: '本月', value: 'month' },
                  { label: '近30天', value: 'last30' },
                ]}
                onChange={handlePreset}
                defaultValue="month"
              />
              <RangePicker value={dateRange} onChange={(d) => d && setDateRange(d)} allowClear={false} />
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新数据</Button>
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none' }}>
                导出CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div style={{ marginTop: 16 }}>
        <Spin spinning={loading}>
        {/* KPI */}
        <Row gutter={[20, 20]}>
          {[
            { title: '消费总额', value: summary.total_cost || 0, icon: <DollarOutlined />, suffix: '元', precision: 2, grad: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
            { title: '总请求数', value: summary.total_requests || 0, icon: <ApiOutlined />, precision: 0, grad: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
            { title: '活跃用户', value: summary.active_users || 0, icon: <TeamOutlined />, precision: 0, grad: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
            { title: '统计天数', value: dateRange[0] && dateRange[1] ? dateRange[1].diff(dateRange[0], 'day') + 1 : 0, icon: <CalendarOutlined />, suffix: '天', precision: 0, grad: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
          ].map((item, idx) => (
            <Col xs={24} sm={12} lg={6} key={idx}>
              <Card bordered={false} className="kpi-card" style={{ background: item.grad, borderRadius: 14 }}>
                <Statistic
                  title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 500 }}>{item.title}</Text>}
                  value={item.value}
                  precision={item.precision}
                  prefix={React.cloneElement(item.icon, { style: { fontSize: 20 } })}
                  suffix={item.suffix ? <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>{item.suffix}</Text> : undefined}
                  valueStyle={{ color: '#fff', fontSize: 30, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* 图表 */}
        <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
          <Col xs={24} lg={14}>
            <Card bordered={false} style={{ borderRadius: 14 }}
              title={<Text strong>每日消费趋势</Text>}
              extra={
                <Segmented
                  size="small"
                  value={trendMode}
                  onChange={setTrendMode}
                  options={[
                    { label: '堆叠面积', value: 'stack' },
                    { label: '折线对比', value: 'line' },
                  ]}
                />
              }>
              {daily.length > 0 ? <ReactECharts option={lineOption} style={{ height: 400 }} /> :
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>暂无数据</div>}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card bordered={false} style={{ borderRadius: 14 }} title={<Text strong>模型消费分布</Text>}>
              {byModel.length > 0 ? <ReactECharts option={pieOption} style={{ height: 400 }} /> :
                <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>暂无数据</div>}
            </Card>
          </Col>
        </Row>

        <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
          <Col xs={24} lg={9}>
            <Card bordered={false} style={{ borderRadius: 14 }} title={<Text strong>模型消费排行</Text>}>
              {byModel.length > 0 ? <ReactECharts option={barOption} style={{ height: 370 }} /> :
                <div style={{ height: 370, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>暂无数据</div>}
            </Card>
          </Col>
          <Col xs={24} lg={15}>
            <Card bordered={false} style={{ borderRadius: 14 }} title={<Text strong>模型消费明细表</Text>}>
              <Table columns={tableColumns} dataSource={byModel} rowKey="model"
                pagination={false} size="small" scroll={{ x: 900, y: 330 }} />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
    </div>
  );
}
